import { z } from 'zod';
import { DMS_MAX_FILE_BYTES } from '../constants/dms.js';
export const LEGACY_DOCUMENT_KINDS = [
    'OFFER',
    'ORDER',
    'DELIVERY',
    'INVOICE',
    'CREDIT_NOTE',
    'CANCELLATION',
    'CORRECTION_INVOICE',
    'INVOICE_UNCLASSIFIED',
    'PURCHASE_ORDER',
    'PURCHASE_INVOICE',
];
export const MIGRATION_MAX_RECORDS = 100_000;
export const MIGRATION_CHUNK_RECORDS = 50;
export const MIGRATION_CHUNK_BYTES = 8 * 1024 * 1024;
export const LEGACY_MAX_ITEMS = 10_000;
export const MIGRATION_SOURCE_IDENTITY_MAX_BYTES = 1000;
function sourceIdentityBytes(value) {
    return Array.from(value).reduce((bytes, character) => {
        const point = character.codePointAt(0) ?? 0;
        return bytes + (point <= 0x7f ? 1 : point <= 0x7ff ? 2 : point <= 0xffff ? 3 : 4);
    }, 0);
}
const sourcePart = z
    .string()
    .min(1)
    .max(255)
    .refine((value) => Array.from(value).every((character) => (character.codePointAt(0) ?? 0) >= 32));
export const legacySourceIdentitySchema = z
    .object({
    system: sourcePart,
    instance: sourcePart,
    tenant: sourcePart,
    entity: sourcePart,
    key: sourcePart,
})
    .strict()
    .refine((value) => sourceIdentityBytes(Object.values(value).join('')) <= MIGRATION_SOURCE_IDENTITY_MAX_BYTES, 'The complete source identity exceeds the indexed UTF-8 byte limit');
export function sameMigrationNamespace(left, right) {
    return (left.system === right.system && left.instance === right.instance && left.tenant === right.tenant);
}
/** Tuple encoding keeps identities unambiguous even when source keys contain separators. */
export function legacySourceKey(input) {
    const value = legacySourceIdentitySchema.parse(input);
    return JSON.stringify([value.system, value.instance, value.tenant, value.entity, value.key]);
}
export const legacyHashSchema = z.string().regex(/^[a-f0-9]{64}$/u);
export const legacyAmountSchema = z.string().regex(/^-?\d{1,14}(?:\.\d{1,4})?$/u);
const sourceDecimal = z
    .string()
    .max(80)
    .regex(/^-?\d+(?:\.\d+)?$/u);
const calendarDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/u)
    .refine((value) => {
    const date = new Date(`${value}T00:00:00.000Z`);
    return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}, 'Invalid calendar date');
const text = z.string().max(1_000_000);
const optionalText = text.nullable();
export const legacyEvidenceSchema = z
    .object({
    sha256: legacyHashSchema,
    mappingVersion: z.string().min(1).max(100),
})
    .strict();
export const legacyFieldSnapshotSchema = z
    .object({
    key: sourcePart,
    label: z.string().min(1).max(500),
    sourceType: z.string().max(100),
    value: optionalText,
    definitionSource: legacySourceIdentitySchema.nullable(),
    // Importing evidence does not grant PDF or customer-portal publication rights.
    pdfVisible: z.literal(false),
    portalVisible: z.literal(false),
})
    .strict();
export const legacyLineSnapshotSchema = z
    .object({
    source: legacySourceIdentitySchema,
    valueSource: legacySourceIdentitySchema,
    position: z.number().int().min(0).max(LEGACY_MAX_ITEMS),
    sourcePosition: optionalText,
    kindCode: optionalText,
    productSource: legacySourceIdentitySchema.nullable(),
    articleNumber: optionalText,
    description: optionalText,
    quantity: sourceDecimal.nullable(),
    inputQuantity: sourceDecimal.nullable(),
    unitCode: optionalText,
    unitLabel: optionalText,
    unitNet: sourceDecimal.nullable(),
    unitGross: sourceDecimal.nullable(),
    discountPercent: sourceDecimal.nullable(),
    discountAmount: sourceDecimal.nullable(),
    taxPercent: sourceDecimal.nullable(),
    net: sourceDecimal.nullable(),
    tax: sourceDecimal.nullable(),
    gross: sourceDecimal.nullable(),
    fields: z.array(legacyFieldSnapshotSchema).max(100),
    evidence: legacyEvidenceSchema,
})
    .strict();
export const legacyPaymentSnapshotSchema = z
    .object({
    source: legacySourceIdentitySchema,
    date: calendarDate.nullable(),
    amount: sourceDecimal.nullable(),
    sourceKind: optionalText,
    description: optionalText,
    evidence: legacyEvidenceSchema,
})
    .strict();
export const legacyDocumentInputSchema = z
    .object({
    attachmentIssues: z
        .array(z
        .object({
        source: legacySourceIdentitySchema,
        reference: legacySourceIdentitySchema,
        originalName: optionalText,
        bytes: z.literal(0),
        reason: z.literal('EMPTY_SOURCE_FILE'),
        evidence: legacyEvidenceSchema,
    })
        .strict())
        .max(100)
        .optional(),
    source: legacySourceIdentitySchema,
    kind: z.enum(LEGACY_DOCUMENT_KINDS),
    number: z.string().max(255).nullable(),
    documentDate: calendarDate.nullable(),
    currency: z.string().regex(/^[A-Z]{3}$/u),
    partnerSource: legacySourceIdentitySchema.nullable(),
    partner: z
        .object({
        nameLines: z.array(z.string().max(1000)).max(10),
        addressLines: z.array(z.string().max(1000)).max(20),
        sourceKey: sourcePart.nullable(),
    })
        .strict(),
    status: z
        .object({
        code: optionalText,
        label: optionalText,
        open: z.boolean().nullable(),
        openBasis: z.enum(['UNKNOWN', 'SOURCE_PAYMENT_STATE']).optional(),
        dimensions: z
            .array(z
            .object({
            key: z.enum(['CHANNEL', 'DELIVERY', 'INVOICING', 'PAYMENT']),
            sourceField: sourcePart,
            code: optionalText,
            sourceLabel: optionalText,
            meaning: z.enum([
                'UNKNOWN',
                'FULLY_DELIVERED',
                'PAYMENT_OPEN',
                'PAYMENT_PARTIAL',
                'PAYMENT_PAID',
                'PAYMENT_REVIEW',
                'PAYMENT_WRITTEN_OFF',
            ]),
        })
            .strict())
            .max(10)
            .optional(),
    })
        .strict(),
    net: legacyAmountSchema.nullable(),
    tax: legacyAmountSchema.nullable(),
    gross: legacyAmountSchema.nullable(),
    headerTexts: z.array(text).max(10),
    fields: z.array(legacyFieldSnapshotSchema).max(100),
    items: z.array(legacyLineSnapshotSchema).max(LEGACY_MAX_ITEMS),
    payments: z.array(legacyPaymentSnapshotSchema).max(LEGACY_MAX_ITEMS),
    evidence: legacyEvidenceSchema,
})
    .strict()
    .superRefine((document, context) => {
    if (document.source.system === 'LEXWARE' &&
        (document.partnerSource !== null ||
            document.items.some((item) => item.productSource !== null))) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Lexware archive evidence cannot link to operational master records',
        });
    }
    const keys = document.items.map((item) => legacySourceKey(item.source));
    if (new Set(keys).size !== keys.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['items'],
            message: 'Duplicate historical line occurrence',
        });
    }
    if (new Set(document.items.map((item) => item.position)).size !== document.items.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['items'],
            message: 'Duplicate historical line position',
        });
    }
    const references = [
        document.partnerSource,
        ...(document.attachmentIssues ?? []).flatMap((issue) => [issue.source, issue.reference]),
        ...document.fields.map((field) => field.definitionSource),
        ...document.payments.map((payment) => payment.source),
        ...document.items.flatMap((item) => [
            item.source,
            item.valueSource,
            item.productSource,
            ...item.fields.map((field) => field.definitionSource),
        ]),
    ];
    if (references.some((reference) => reference !== null && !sameMigrationNamespace(document.source, reference))) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A reference belongs to another source installation or tenant',
        });
    }
});
export const legacyDocumentHeaderSnapshotSchema = legacyDocumentInputSchema
    .innerType()
    .omit({ items: true });
export const legacyRelationInputSchema = z
    .object({
    source: legacySourceIdentitySchema,
    left: legacySourceIdentitySchema,
    right: legacySourceIdentitySchema,
    kind: z.string().min(1).max(100),
    evidence: legacyEvidenceSchema,
})
    .strict()
    .refine((input) => [input.left, input.right].every((ref) => sameMigrationNamespace(input.source, ref)), 'Relationship endpoints must belong to the same source installation and tenant');
export const migrationSourceFileSchema = z
    .object({
    source: legacySourceIdentitySchema,
    sha256: legacyHashSchema,
    bytes: z.number().int().nonnegative().max(DMS_MAX_FILE_BYTES),
    originalName: z.string().min(1).max(500),
    evidence: legacyEvidenceSchema,
})
    .strict();
export const legacyArtifactInputSchema = z
    .object({
    source: legacySourceIdentitySchema,
    document: legacySourceIdentitySchema,
    file: legacySourceIdentitySchema,
    sha256: legacyHashSchema,
    bytes: z.number().int().nonnegative().max(DMS_MAX_FILE_BYTES),
    originalName: z.string().min(1).max(500),
    role: z.enum(['ORIGINAL', 'SOURCE_ATTACHMENT']),
    evidence: legacyEvidenceSchema,
})
    .strict()
    .refine((input) => [input.document, input.file].every((ref) => sameMigrationNamespace(input.source, ref)), 'Artifact references must belong to the same source installation and tenant');
//# sourceMappingURL=legacy-document.js.map